/**
 * API Service
 * Handles all communication with the backend
 */

import supabase from '../supabaseClient';
import { captureAPIError } from '../utils/errorLogger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Get authentication headers
 */
async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  return {
    'Authorization': token ? `Bearer ${token}` : '',
  };
}

/**
 * Handle API response and log errors automatically
 * @param {Response} response - Fetch response
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method
 * @param {string} action - User action description
 * @returns {Promise<Object>} Parsed response
 */
async function handleApiResponse(response, endpoint, method, action) {
  if (!response.ok) {
    let errorBody = {};
    try {
      errorBody = await response.json();
    } catch (e) {
      errorBody = { message: response.statusText || 'Unknown error' };
    }

    const errorMessage = errorBody.message || errorBody.error || `${action} failed`;
    const error = new Error(errorMessage);

    // Automatically log the API error
    captureAPIError(error, {
      endpoint: endpoint,
      method: method,
      status: response.status,
      responseBody: errorBody,
      page: action,
      url: `${API_URL}${endpoint}`
    });

    throw error;
  }

  return response.json();
}

/**
 * Upload receipt image
 * @param {File} file - Receipt image file
 * @returns {Promise<Object>} Expense data
 */
export async function uploadReceipt(file) {
  const authHeaders = await getAuthHeaders();
  const formData = new FormData();
  formData.append('receipt', file);

  const response = await fetch(`${API_URL}/api/upload-receipt`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
  });

  return handleApiResponse(response, '/api/upload-receipt', 'POST', 'Upload Receipt');
}

/**
 * Ask AI assistant a question
 * @param {string} query - User's question
 * @returns {Promise<Object>} AI response
 */
export async function askQuestion(query) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/ask`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query }),
  });

  return handleApiResponse(response, '/api/ask', 'POST', 'Ask AI');
}

/**
 * Get expenses with optional filters
 * @param {Object} filters - Filter options
 * @returns {Promise<Object>} Expenses list
 */
export async function getExpenses(filters = {}) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams();

  if (filters.category) params.append('category', filters.category);
  if (filters.currency) params.append('currency', filters.currency);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.minAmount !== undefined && filters.minAmount !== '') params.append('minAmount', filters.minAmount);
  if (filters.maxAmount !== undefined && filters.maxAmount !== '') params.append('maxAmount', filters.maxAmount);
  if (filters.merchant) params.append('merchant', filters.merchant);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
  if (filters.hasReceipt) params.append('hasReceipt', 'true');

  const response = await fetch(`${API_URL}/api/expenses?${params.toString()}`, {
    headers: authHeaders,
  });

  return handleApiResponse(response, '/api/expenses', 'GET', 'Get Expenses');
}

/**
 * Delete an expense
 * @param {string} id - Expense ID
 * @returns {Promise<Object>} Success response
 */
export async function deleteExpense(id) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/expenses/${id}`, {
    method: 'DELETE',
    headers: authHeaders,
  });

  return handleApiResponse(response, `/api/expenses/${id}`, 'DELETE', 'Delete Expense');
}

/**
 * Get a single expense by ID
 * @param {string} id - Expense ID
 * @returns {Promise<Object>} Expense
 */
export async function getExpenseById(id) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/expenses/${id}`, {
    headers: authHeaders,
  });

  return handleApiResponse(response, `/api/expenses/${id}`, 'GET', 'Get Expense');
}

/**
 * Get receipt image URL for an expense
 * @param {string} id - Expense ID
 * @returns {Promise<Object>} { imageUrl, expiresIn }
 */
export async function getReceiptImage(id) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/expenses/${id}/receipt-image`, {
    headers: authHeaders,
  });

  return handleApiResponse(response, `/api/expenses/${id}/receipt-image`, 'GET', 'Get Receipt Image');
}

/**
 * Rotate receipt image
 * @param {string} id - Expense ID
 * @param {number} degrees - Rotation degrees (90, 180, 270)
 * @returns {Promise<Object>} { success, message }
 */
export async function rotateReceiptImage(id, degrees) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/expenses/${id}/rotate-image`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ degrees }),
  });

  return handleApiResponse(response, `/api/expenses/${id}/rotate-image`, 'POST', 'Rotate Receipt Image');
}

/**
 * Create manual expense
 * @param {Object} expenseData - Expense data
 * @returns {Promise<Object>} Created expense
 */
export async function createExpense(expenseData) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/expenses`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(expenseData),
  });

  return handleApiResponse(response, '/api/expenses', 'POST', 'Create Expense');
}

/**
 * Upload bank/credit card statement
 * @param {File} file - Statement file (PDF or image)
 * @returns {Promise<Object>} Import result with summary
 */
export async function uploadBankStatement(file, year = null, signal = null) {
  const authHeaders = await getAuthHeaders();
  const formData = new FormData();
  formData.append('statement', file);

  let url = `${API_URL}/api/upload-bank-statement`;
  if (year) {
    url += `?year=${year}`;
  }

  const fetchOptions = {
    method: 'POST',
    headers: authHeaders,
    body: formData,
  };
  if (signal) fetchOptions.signal = signal;

  const response = await fetch(url, fetchOptions);

  return handleApiResponse(response, '/api/upload-bank-statement', 'POST', 'Upload Bank Statement');
}

/**
 * Undo a statement upload (delete all imported transactions from that batch)
 * @param {string} batchCreatedAt - ISO timestamp of when the batch was created
 * @returns {Promise<Object>} Result with deletedCount
 */
export async function undoStatementUpload(batchCreatedAt) {
  try {
    const authHeaders = await getAuthHeaders();
    const payload = JSON.stringify({ batchCreatedAt });

    return await new Promise((resolve) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('DELETE', `${API_URL}/api/undo-statement`, true);
        xhr.timeout = 10000;
        xhr.setRequestHeader('Content-Type', 'application/json');
        if (authHeaders.Authorization) {
          xhr.setRequestHeader('Authorization', authHeaders.Authorization);
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText || '{}');
              resolve(data);
            } catch (e) {
              resolve({ error: 'Invalid server response' });
            }
            return;
          }

          try {
            const error = JSON.parse(xhr.responseText || '{}');
            resolve({ error: error.message || 'Failed to undo statement upload' });
          } catch (e) {
            resolve({ error: 'Failed to undo statement upload' });
          }
        };

        xhr.onerror = () => resolve({ error: 'Failed to reach server. Please check your connection.' });
        xhr.ontimeout = () => resolve({ error: 'Request timed out. Please try again.' });
        xhr.send(payload);
      } catch (e) {
        resolve({ error: 'Failed to reach server. Please check your connection.' });
      }
    });
  } catch (error) {
    return { error: 'Failed to reach server. Please check your connection.' };
  }
}

/**
 * Update an expense
 * @param {string} id - Expense ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated expense
 */
export async function updateExpense(id, updates) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/expenses/${id}`, {
    method: 'PUT',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  return handleApiResponse(response, `/api/expenses/${id}`, 'PUT', 'Update Expense');
}

/**
 * Bulk delete expenses
 * @param {Array<string>} ids - Array of expense IDs to delete
 * @returns {Promise<Object>} Deletion result
 */
export async function bulkDeleteExpenses(ids) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/expenses/bulk-delete`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ids }),
  });

  return handleApiResponse(response, '/api/expenses/bulk-delete', 'POST', 'Bulk Delete Expenses');
}

/**
 * Check server health
 * @returns {Promise<Object>} Health status
 */
export async function checkHealth() {
  return new Promise((resolve) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${API_URL}/health`, true);
      xhr.timeout = 3000;
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ status: 'online' });
        } else {
          resolve({ status: 'offline' });
        }
      };
      xhr.onerror = () => resolve({ status: 'offline' });
      xhr.ontimeout = () => resolve({ status: 'offline' });
      xhr.send();
    } catch (error) {
      resolve({ status: 'offline' });
    }
  });
}

/**
 * Get monthly spending summary
 * @param {string} month - Month in YYYY-MM format (optional)
 * @returns {Promise<Object>} Monthly summary data
 */
export async function getMonthlySummary(month = null) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams();
  if (month) params.append('month', month);

  const response = await fetch(`${API_URL}/api/analytics/monthly-summary?${params.toString()}`, {
    headers: authHeaders,
  });

  return handleApiResponse(response, '/api/analytics/monthly-summary', 'GET', 'Get Monthly Summary');
}

/**
 * Get category breakdown
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} Category breakdown data
 */
export async function getCategoryBreakdown(startDate, endDate) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const response = await fetch(`${API_URL}/api/analytics/category-breakdown?${params.toString()}`, {
    headers: authHeaders,
  });

  return handleApiResponse(response, '/api/analytics/category-breakdown', 'GET', 'Get Category Breakdown');
}

/**
 * Get top merchants
 * @param {number} limit - Number of merchants to return
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} Top merchants data
 */
export async function getTopMerchants(limit = 10, startDate = null, endDate = null) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams();
  params.append('limit', limit);
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const response = await fetch(`${API_URL}/api/analytics/top-merchants?${params.toString()}`, {
    headers: authHeaders,
  });

  return handleApiResponse(response, '/api/analytics/top-merchants', 'GET', 'Get Top Merchants');
}

/**
 * Get recurring expenses
 * @param {number} minOccurrences - Minimum occurrences
 * @param {number} amountTolerance - Amount tolerance (0.1 = ±10%)
 * @returns {Promise<Object>} Recurring expenses data
 */
export async function getRecurringExpenses(minOccurrences = 3, amountTolerance = 0.1) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams();
  params.append('minOccurrences', minOccurrences);
  params.append('amountTolerance', amountTolerance);

  const response = await fetch(`${API_URL}/api/analytics/recurring?${params.toString()}`, {
    headers: authHeaders,
  });

  return handleApiResponse(response, '/api/analytics/recurring', 'GET', 'Get Recurring Expenses');
}

/**
 * Get spending anomalies
 * @param {number} stdDevMultiplier - Standard deviation multiplier
 * @param {number} limit - Number of anomalies to return
 * @returns {Promise<Object>} Anomalies data
 */
export async function getAnomalies(stdDevMultiplier = 2, limit = 10) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams();
  params.append('stdDevMultiplier', stdDevMultiplier);
  params.append('limit', limit);

  const response = await fetch(`${API_URL}/api/analytics/anomalies?${params.toString()}`, {
    headers: authHeaders,
  });

  return handleApiResponse(response, '/api/analytics/anomalies', 'GET', 'Get Anomalies');
}

/**
 * Get spending trends
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {string} granularity - Granularity (daily or monthly)
 * @returns {Promise<Object>} Trends data
 */
export async function getSpendingTrends(startDate, endDate, granularity = 'daily') {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  params.append('granularity', granularity);

  const response = await fetch(`${API_URL}/api/analytics/trends?${params.toString()}`, {
    headers: authHeaders,
  });

  return handleApiResponse(response, '/api/analytics/trends', 'GET', 'Get Spending Trends');
}

/**
 * Get budget vs actual analysis
 * @param {Object} budgets - Budget data { category: amount, ... }
 * @param {string} month - Month in YYYY-MM format (optional)
 * @returns {Promise<Object>} Budget analysis data
 */
export async function getBudgetAnalysis(budgets, month = null) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/analytics/budget`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      budgets,
      month,
    }),
  });

  return handleApiResponse(response, '/api/analytics/budget', 'POST', 'Get Budget Analysis');
}

/**
 * Get comprehensive analytics dashboard
 * @param {string} month - Month in YYYY-MM format (optional)
 * @param {Object} budgets - Budget data (optional)
 * @param {boolean} includeAdvanced - Include advanced analytics
 * @returns {Promise<Object>} Dashboard data
 */
export async function getAnalyticsDashboard(month = null, budgets = {}, includeAdvanced = true) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams();
  if (month) params.append('month', month);
  if (Object.keys(budgets).length > 0) params.append('budgets', JSON.stringify(budgets));
  params.append('includeAdvanced', includeAdvanced);

  const response = await fetch(`${API_URL}/api/analytics/dashboard?${params.toString()}`, {
    headers: authHeaders,
  });

  return handleApiResponse(response, '/api/analytics/dashboard', 'GET', 'Get Analytics Dashboard');
}
